import { Link } from "react-router-dom";
import { ArrowRight, Phone, Check, MapPin, CalendarDays, FileText } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import CTABanner from "../components/CTABanner";
import { useSEO } from "../hooks/useSEO";
import { villes, communeSlug } from "../data/villes";
import { site } from "../data/site";

export default function Ville({ ville }) {
  const v = villes[ville];
  const others = Object.values(villes).filter((x) => x.slug !== v.slug);

  useSEO({ title: v.seoTitle, description: v.seoDesc });

  return (
    <>
      <PageHeader eyebrow="Zone d'intervention" title={v.headerTitle} description={v.headerDesc} />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <SectionHeading eyebrow={`Embek Group à ${v.name}`} title={`Un entretien de jardin soigné, à ${v.name}`} />
              <Reveal delay={0.15}>
                {v.intro.map((p, i) => (
                  <p key={i} className="mt-5 text-coal-muted font-light leading-relaxed">
                    {p}
                  </p>
                ))}
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a href={site.phoneHref} data-testid={`ville-phone-${ville}`} className="btn-primary">
                    <Phone size={15} /> Appeler
                  </a>
                  <Link to="/demander-un-devis" data-testid={`ville-devis-${ville}`} className="btn-outline-forest">
                    <FileText size={15} /> Demander un devis
                  </Link>
                  <Link to="/rendez-vous" data-testid={`ville-rdv-${ville}`} className="btn-outline-forest">
                    <CalendarDays size={15} /> Prendre rendez-vous
                  </Link>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <div className="rounded-3xl overflow-hidden border border-forest/10 shadow-lg shadow-forest/10 h-[360px]">
                <iframe
                  title={`Zone d'intervention Embek Group à ${v.name}`}
                  src={`https://www.google.com/maps?q=${v.mapQuery}&z=11&hl=fr&output=embed`}
                  className="w-full h-full grayscale-[0.3]"
                  loading="lazy"
                  data-testid={`ville-map-${ville}`}
                />
              </div>
            </Reveal>
          </div>

          {/* Services */}
          <div className="mt-20">
            <SectionHeading
              eyebrow="Nos prestations"
              title={`Que faisons-nous à ${v.name} ?`}
            />
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {v.services.map((s, i) => (
                <Reveal key={s} delay={(i % 3) * 0.06} className="h-full">
                  <Link
                    to="/services"
                    data-testid={`ville-service-${ville}-${i}`}
                    className="group h-full flex items-center gap-3.5 bg-white rounded-2xl border border-forest/10 px-5 py-4 shadow-sm hover:shadow-lg hover:shadow-forest/10 hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <span className="w-8 h-8 rounded-full bg-moss/15 text-moss flex items-center justify-center shrink-0">
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <span className="text-sm font-semibold text-forest">{s}</span>
                    <ArrowRight size={14} className="ml-auto text-moss opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Communes desservies */}
          <div className="mt-20">
            <SectionHeading
              eyebrow="Communes desservies"
              title={`Nous intervenons à ${v.name} et alentours`}
              description="Votre commune figure dans la liste ? Contactez-nous pour planifier une intervention."
            />
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-2" data-testid={`ville-communes-${ville}`}>
                {v.communes.map((c, i) => (
                  <Link
                    key={i}
                    to={`/jardinier-${communeSlug(c)}`}
                    data-testid={`ville-commune-${ville}-${i}`}
                    className="px-3.5 py-1.5 rounded-full border border-forest/15 bg-white text-[12.5px] font-medium text-forest/80 flex items-center gap-1.5 hover:border-moss/50 hover:text-forest transition-colors"
                  >
                    <MapPin size={12} className="text-moss" /> {c}
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Autres zones */}
          <Reveal className="mt-16">
            <div className="bg-forest-dark grain rounded-3xl px-8 py-10 text-center" data-testid={`ville-others-${ville}`}>
              <h3 className="font-serif text-2xl sm:text-3xl text-sand">Nous intervenons aussi à :</h3>
              <div className="mt-5 flex flex-wrap justify-center gap-4">
                {others.map((o) => (
                  <Link
                    key={o.slug}
                    to={`/${o.slug}`}
                    data-testid={`ville-other-${o.name.toLowerCase()}`}
                    className="px-6 py-2.5 rounded-full border border-sand/25 text-sand text-sm font-semibold hover:bg-sand hover:text-forest transition-colors"
                  >
                    Jardinier à {o.name}
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
